import * as React from "react"
import { List, Box } from "@chakra-ui/react"
import Badge from "./_badge"
import HistoryBadge from "./_historyBadge"
import {
  badgesFilterState,
  badgeListSelector
} from "../../../recoil/atoms/badges"
import { useRecoilValue } from "recoil"
import styled from "@emotion/styled"
import {
  badgeHistoryListSelector,
  badgesHistoryFilterState
} from "../../../recoil/atoms/badgeHistory"
import type { Room } from "../../../recoil/atoms/room/types"
import { brandingSelector } from "../../../recoil/atoms/room"
import { useLocalUserType } from "../../../hooks/useLocalUserType"
import { FadeBadges } from "./_fadeBadges"
import SearchBar from "./components/SearchBar"
import { useRecoilState } from "recoil"
import {
  taggedBadgesListSelector,
  taggedBadgesFilterState
} from "../../../recoil/atoms/taggedBadges"
import { shoppableMNPVideoAtom } from "../../../recoil/atoms/shoppableVideos"
import { useTranslation } from "react-i18next"
import layoutStates from "../../../recoil/atoms/layout"
import { toPixelString } from "../../../util"

interface StyledProps {
  room?: Room
}
const Container = styled(List)<StyledProps>`
  &::-webkit-scrollbar-track {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorder || "#cbd5e0"};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorderOffset || "#fff"};
    border-radius: 10px;
  }
`
const BadgesList = React.memo(() => {
  const badgesList = useRecoilValue(badgeListSelector)
  const roomStyles = useRecoilValue(brandingSelector)
  const [value, setValue] = useRecoilState(badgesFilterState)
  const { t } = useTranslation()
  return (
    <>
      <SearchBar value={value} setValue={setValue} />
      <Box
        p="1rem 0.5rem 1rem 0rem"
        height="100%"
        overflowX="hidden"
        overflowY="hidden"
      >
        <Container
          roomStyles={roomStyles}
          overflowX="hidden"
          w="100%"
          p="0rem 0.5rem 0 1rem"
          overflowY="auto"
          scrollbarwidth="thin"
          style={{ scrollBehavior: "smooth" }}
          spacing={4}
          h="100%"
        >
          {badgesList?.map(badge => (
            <Badge badge={badge} />
          ))}
          {badgesList.length === 0 && (
            <FadeBadges
              infoText={t(
                "This is where products available for purchase in your rooms will appear once they have been added via the Dashboard. Products can be added manually or imported from online store providers such as Shopify."
              )}
            />
          )}
          ; {/** TODO: confirm badgeIdList is string array  */}
        </Container>
      </Box>
    </>
  )
})

const HistoryList = React.memo(() => {
  const { t } = useTranslation()
  const allBadgeHistory = useRecoilValue(badgeHistoryListSelector)
  const [taggedValue, setTaggedValue] = useRecoilState(taggedBadgesFilterState)
  const roomStyles = useRecoilValue(brandingSelector)
  const { isModerator } = useLocalUserType()
  const [value, setValue] = useRecoilState(badgesHistoryFilterState)
  /*     */
  const { enabledShoppableMNP } = useRecoilValue(shoppableMNPVideoAtom)
  const taggedBadgesList = useRecoilValue(taggedBadgesListSelector)
  const isSidePanelWithSingleTab = useRecoilValue(
    layoutStates.isSidePanelWithSingleTab
  )

  const desktopBadgesHistory = React.useMemo(() => {
    return enabledShoppableMNP && !isModerator
      ? taggedBadgesList
      : allBadgeHistory
  }, [allBadgeHistory, enabledShoppableMNP, isModerator, taggedBadgesList])
  return (
    <>
      <SearchBar
        value={enabledShoppableMNP && !isModerator ? taggedValue : value}
        setValue={
          enabledShoppableMNP && !isModerator ? setTaggedValue : setValue
        }
      />
      <Box
        p="1rem 0.5rem 1rem 0"
        height="100%"
        overflowX="hidden"
        overflowY="hidden"
        paddingBottom={isSidePanelWithSingleTab ? "70px" : undefined} // 70px to make same as chat list
      >
        <Container
          roomStyles={roomStyles}
          overflowX="hidden"
          w="100%"
          p="0rem 0.5rem 0 1rem"
          overflowY="auto"
          scrollbarwidth="thin"
          style={{ scrollBehavior: "smooth" }}
          spacing={4}
          h="100%"
        >
          {desktopBadgesHistory?.map(singleBadgeData => (
            <HistoryBadge
              badgeHistoryData={singleBadgeData}
              key={singleBadgeData.id}
              isModerator={isModerator}
              customStyles={{
                button: {
                  borderRadius: toPixelString(
                    roomStyles?.badgeButtonBorderRadius
                  ),
                  _hoverStyles: {
                    borderColor:
                      roomStyles?.badgeButtonBorderMouseoverColor || null
                  }
                }
              }}
            />
          ))}
          {desktopBadgesHistory.length === 0 && (
            <FadeBadges
              infoText={t(
                "Featured products shown here are those items that you have shown as video overlay badges via the LiveSell tab. Audience members have a single <i>Shop</i> tab which will display these Featured products, or Tagged products, if they are watching a shoppable video which has had products tagged to it."
              )}
            />
          )}
          ; {/** TODO: confirm badgeIdList is string array  */}
        </Container>
      </Box>
    </>
  )
})

export { BadgesList, HistoryList }
